<template>
  <v-container fluid class="series-page pa-4">
    <v-layout align-center class="mb-4">
      <div>
        <div class="headline">Artwork Series</div>
        <div class="caption grey--text">Curated, ordered groups of artworks for the catalogue.</div>
      </div>
      <v-spacer></v-spacer>
      <v-btn color="black" dark @click="openEditor()"><v-icon left>add</v-icon>New series</v-btn>
    </v-layout>

    <v-card flat class="series-table">
      <v-card-title>
        <v-text-field v-model="search" append-icon="search" label="Search series" single-line hide-details clearable></v-text-field>
      </v-card-title>
      <v-data-table :headers="headers" :items="series" :search="search" item-key="id" :loading="loading">
        <template slot="items" slot-scope="props">
          <tr @click="props.expanded = !props.expanded" class="series-row">
            <td><v-icon small>{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon></td>
            <td>{{ props.item.title_en }}</td>
            <td>{{ props.item.title_es || '—' }}</td>
            <td>{{ props.item.artworks.length }}</td>
            <td><v-chip small :color="props.item.published ? 'green lighten-4' : 'grey lighten-3'">{{ props.item.published ? 'Published' : 'Draft' }}</v-chip></td>
            <td class="text-xs-right" @click.stop>
              <v-btn icon small @click="openEditor(props.item)"><v-icon small>edit</v-icon></v-btn>
              <v-btn icon small @click="removeSeries(props.item)"><v-icon small>delete</v-icon></v-btn>
            </td>
          </tr>
        </template>
        <template slot="expand" slot-scope="props">
          <v-card flat class="grey lighten-4 px-5 py-3">
            <div v-if="!props.item.artworks.length" class="caption grey--text">No artworks assigned.</div>
            <div v-for="(artwork, index) in props.item.artworks" :key="artwork.id" class="series-artwork-line">
              <span class="caption grey--text mr-3">{{ index + 1 }}</span>
              <strong>{{ artwork.title }}</strong>
              <span class="ml-2 grey--text">{{ artwork.artist_name }} · {{ artwork.accession_number }}</span>
            </div>
          </v-card>
        </template>
        <template slot="no-data">
          <index-load-state
            :loading="loading"
            :error="seriesLoadError"
            empty-text="No Artwork Series Found."
          ></index-load-state>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="editorOpen" max-width="1050" persistent scrollable>
      <v-card v-if="draft">
        <v-card-title>
          <span class="headline">{{ draft.id ? 'Edit' : 'New' }} artwork series</span>
          <v-spacer></v-spacer>
          <v-switch v-model="draft.published" label="Published" color="black" hide-details></v-switch>
          <v-btn icon @click="editorOpen = false"><v-icon>close</v-icon></v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text>
          <v-tabs v-model="languageTab" color="transparent" slider-color="black">
            <v-tab>English</v-tab><v-tab>Español</v-tab>
            <v-tab-item v-for="locale in ['en', 'es']" :key="locale">
              <v-container fluid class="px-0">
                <v-text-field v-model="draft['title_' + locale]" :label="'Title (' + locale.toUpperCase() + ')'" required></v-text-field>
                <v-text-field v-model="draft['slug_' + locale]" :label="'URL slug (' + locale.toUpperCase() + ')'" hint="Generated from title when left blank"></v-text-field>
                <v-textarea v-model="draft['introduction_' + locale]" label="Short introduction" auto-grow rows="2"></v-textarea>
                <label class="editor-label">Full description</label>
                <text-editor :value="draft['body_' + locale]" @input="draft['body_' + locale] = $event"></text-editor>
              </v-container>
            </v-tab-item>
          </v-tabs>

          <v-divider class="my-4"></v-divider>
          <h3 class="title mb-3">Artworks</h3>
          <v-autocomplete
            v-model="artworkToAdd"
            :items="availableArtworks"
            :loading="artworksLoading"
            :error-messages="artworksLoadError"
            item-value="id"
            item-text="searchLabel"
            label="Search and add an artwork"
            clearable
            append-outer-icon="add"
            @click:append-outer="addArtwork"
            @keyup.enter="addArtwork"
          ></v-autocomplete>
          <v-btn v-if="artworksLoadError" flat small :loading="artworksLoading" @click="loadArtworks">Retry loading artworks</v-btn>

          <v-list two-line class="ordered-artworks">
            <v-list-tile v-for="(artwork, index) in draft.artworks" :key="artwork.id">
              <v-list-tile-action><span class="position">{{ index + 1 }}</span></v-list-tile-action>
              <v-list-tile-content>
                <v-list-tile-title>{{ artwork.title }}</v-list-tile-title>
                <v-list-tile-sub-title>{{ artwork.artist_name }} · {{ artwork.accession_number }}</v-list-tile-sub-title>
              </v-list-tile-content>
              <v-list-tile-action class="order-actions">
                <v-btn icon small :disabled="index === 0" @click="move(index, -1)"><v-icon small>arrow_upward</v-icon></v-btn>
                <v-btn icon small :disabled="index === draft.artworks.length - 1" @click="move(index, 1)"><v-icon small>arrow_downward</v-icon></v-btn>
                <v-btn icon small @click="draft.artworks.splice(index, 1)"><v-icon small>close</v-icon></v-btn>
              </v-list-tile-action>
            </v-list-tile>
          </v-list>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn flat @click="editorOpen = false">Cancel</v-btn>
          <v-btn color="black" dark :loading="saving" @click="save">Save series</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" right top :color="snackbarColor">{{ snackbarText }}</v-snackbar>
  </v-container>
</template>

<script>
import axios from '@/utils/apiClient'
import TextEditor from '@/components/artworks/TextEditor'
import IndexLoadState from '@/components/general/IndexLoadState.vue'

const emptySeries = () => ({
  title_en: '', title_es: '', slug_en: '', slug_es: '',
  introduction_en: '', introduction_es: '', body_en: '', body_es: '',
  published: false, artworks: []
})

export default {
  name: 'ArtworkSeries',
  components: { TextEditor, IndexLoadState },
  data () {
    return {
      series: [], artworks: [], draft: null, search: '', loading: true,
      seriesLoadError: '', artworksLoadError: '',
      artworksLoading: true, saving: false, editorOpen: false, languageTab: 0,
      artworkToAdd: null, snackbar: false, snackbarText: '', snackbarColor: 'black',
      headers: [
        { text: '', value: '', sortable: false, width: '40px' },
        { text: 'English title', value: 'title_en' },
        { text: 'Spanish title', value: 'title_es' },
        { text: 'Artworks', value: 'artworks.length' },
        { text: 'Status', value: 'published' },
        { text: '', value: '', sortable: false }
      ]
    }
  },
  computed: {
    availableArtworks () {
      const selected = new Set((this.draft && this.draft.artworks || []).map(a => a.id))
      return this.artworks.filter(a => !selected.has(a.id))
    }
  },
  created () { this.loadSeries(); this.loadArtworks() },
  watch: {
    '$route.query' () {
      this.openRouteRequestedEditor()
    }
  },
  methods: {
    loadSeries () {
      this.loading = true
      this.seriesLoadError = ''
      axios.get(`${this.$globals.apiserver}/artwork_series.json`)
        .then(response => { this.series = response.data })
        .catch(() => {
          this.seriesLoadError = 'Could not load Artwork Series.'
          this.notify(this.seriesLoadError, 'red')
        })
        .then(() => { this.loading = false })
    },
    loadArtworks () {
      this.artworksLoading = true
      this.artworksLoadError = ''
      return axios.get(`${this.$globals.apiserver}/artworks.json`)
        .then(response => {
          this.artworks = response.data.map(a => ({
            id: a.id, title: a.title, accession_number: a.accession_number,
            artist_name: a.full_name || a.artist_name || '',
            searchLabel: `${a.title || 'Untitled'} — ${a.full_name || a.artist_name || ''} (${a.accession_number || ''})`
          }))
          this.artworksLoading = false
          this.openRouteRequestedEditor()
        })
        .catch(() => {
          this.artworksLoadError = 'Could not load artworks.'
          this.notify(this.artworksLoadError, 'red')
        })
        .then(() => { this.artworksLoading = false })
    },
    openRouteRequestedEditor () {
      if (!this.$route.query.new || !this.$route.query.artwork_ids || this.artworksLoading) return

      const artworkIds = this.$route.query.artwork_ids
        .split(',')
        .map(id => parseInt(id, 10))
        .filter(id => !isNaN(id))
      const selectedArtworks = artworkIds
        .map(id => this.artworks.find(artwork => artwork.id === id))
        .filter(artwork => artwork)

      this.openEditor()
      this.draft.artworks = selectedArtworks.map(artwork => Object.assign({}, artwork))
    },
    openEditor (record) {
      this.draft = record ? JSON.parse(JSON.stringify(record)) : emptySeries()
      this.editorOpen = true; this.languageTab = 0; this.artworkToAdd = null
    },
    addArtwork () {
      const artwork = this.artworks.find(a => a.id === this.artworkToAdd)
      if (artwork && !this.draft.artworks.some(a => a.id === artwork.id)) this.draft.artworks.push(Object.assign({}, artwork))
      this.artworkToAdd = null
    },
    move (index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.draft.artworks.length) return
      const item = this.draft.artworks.splice(index, 1)[0]
      this.draft.artworks.splice(target, 0, item)
    },
    save () {
      this.saving = true
      const payload = Object.assign({}, this.draft, { artwork_ids: this.draft.artworks.map(a => a.id) })
      delete payload.artworks
      const request = this.draft.id
        ? axios.patch(`${this.$globals.apiserver}/artwork_series/${this.draft.id}.json`, { artwork_series: payload })
        : axios.post(`${this.$globals.apiserver}/artwork_series.json`, { artwork_series: payload })
      request.then(() => { this.editorOpen = false; this.loadSeries(); this.notify('Artwork series saved.', 'black') })
        .catch(error => this.notify((error.response && error.response.data.errors || ['Could not save series.']).join(', '), 'red'))
        .then(() => { this.saving = false })
    },
    removeSeries (record) {
      if (!window.confirm(`Delete “${record.title_en}”?`)) return
      axios.delete(`${this.$globals.apiserver}/artwork_series/${record.id}.json`)
        .then(() => { this.loadSeries(); this.notify('Artwork series deleted.', 'black') })
        .catch(() => this.notify('Could not delete series.', 'red'))
    },
    notify (text, color) { this.snackbarText = text; this.snackbarColor = color; this.snackbar = true }
  }
}
</script>

<style scoped>
.series-page { max-width: 1500px; }
.series-table { border-top: 1px solid #000; }
.series-row { cursor: pointer; }
.series-artwork-line { padding: 5px 0; border-bottom: 1px solid rgba(0,0,0,.08); }
.editor-label { display: block; margin: 12px 0 8px; color: rgba(0,0,0,.54); font-size: 12px; }
.ordered-artworks { border-top: 1px solid rgba(0,0,0,.2); }
.position { display: inline-flex; width: 28px; height: 28px; align-items: center; justify-content: center; border: 1px solid #000; border-radius: 50%; }
.order-actions { flex-direction: row; min-width: 120px; }
</style>



// WEBPACK FOOTER //
// src/components/series/ArtworkSeries.vue